import getDynamicContentPaths, {
  getModuleOrDefault,
} from '@optum-fpc-psx-mobile-apps/mapping-files-translation-transperfect';
import enLangugages from '@optum-fpc-psx-mobile-apps/mapping-files-translation-transperfect/data/en/languageTranslations.json';
import { t } from 'i18next';
import _ from 'lodash';
import { useEffect, useState } from 'react';

import { useLagoon } from '../../../hooks/useLagoon';
import { removeSpecialCharactersUsingMatch } from '../../../utils/smartChoice.utils';

export const getLanguagesSpokenValues = async (
  locale: string,
  pesLangs: string
): Promise<Array<{ title: string; value: string }>> => {
  const dynamicContent = await getDynamicContentPaths(locale);
  const languagesValues = dynamicContent.language;

  const pesLangsFormatted =
    pesLangs?.split(',')?.map((lang: string) => lang.trim()?.toLowerCase()) ??
    [];
  return Object.keys(languagesValues).reduce((acc: any, key: string) => {
    if (pesLangsFormatted?.includes(key?.trim()?.toLowerCase())) {
      acc.push({ title: languagesValues[key], value: key });
    }
    return acc;
  }, []);
};

export const getSelectedLanguage = async (keys: string[], language: string) => {
  const dynamicContent = await getDynamicContentPaths(language);
  const languagesValues = dynamicContent.language;

  return keys.map((key) => languagesValues[key] || enLangugages[key]);
};

export const useLanguageTranslations = (language: string) => {
  const [languagesMap, setLanguageMap] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchLanguageMap = async () => {
      try {
        const fetchedLanguageMap = await getModuleOrDefault(
          language,
          'languageTranslations'
        );
        setLanguageMap(
          Array.isArray(fetchedLanguageMap)
            ? Object.fromEntries(
                fetchedLanguageMap.map((item) => [item.key, item.value])
              )
            : fetchedLanguageMap
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching language translations:', error);
      }
    };

    fetchLanguageMap();
  }, [language]);

  return languagesMap;
};

export const getSelectedGender = (
  keys: string[],
  list: { value: string; title: string }[]
): string[] => {
  const selectedGender = keys.map((key) => {
    const index = list.findIndex((item) => item.value === key);
    if (index !== -1) {
      return list[index]?.title;
    }
    return '';
  });
  return selectedGender.filter((item) => item !== '');
};

export const deleteCommonKeys = (formFields) => {
  delete formFields.createdDate;
  delete formFields.lastUpdatedDate;
  delete formFields.memberRandomizedKey;
  delete formFields.uuid;
  delete formFields.hasSetPreferences;
  delete formFields.hasDismissedCoachMark;
  delete formFields.hasDismissedCoachMarkWeb;
  delete formFields.hasDefaultPreferences;
  delete formFields.hasDismissedPopover;

  return formFields;
};

export const countDifferences = (oldValues, newValues) => {
  const formattedOldValues = deleteCommonKeys(
    JSON.parse(JSON.stringify(oldValues))
  );
  const formattedNewValues = deleteCommonKeys(
    JSON.parse(JSON.stringify(newValues))
  );

  const allKeys = Object.keys(formattedOldValues);
  let differenceCount = 0;

  _.forEach(allKeys, (key) => {
    const oldValue = formattedOldValues[key];
    const newValue = formattedNewValues[key];

    if (oldValue !== newValue && typeof oldValue !== 'object') {
      differenceCount++;
    } else if (
      typeof oldValue === 'object' &&
      oldValue?.length !== newValue?.length
    ) {
      differenceCount++;
    } else if (typeof oldValue === 'object') {
      const difference = _.difference(oldValue, newValue);
      if (difference.length > 0) {
        differenceCount++;
      }
    }
  });

  return differenceCount;
};

export const countPreferences = (preferences) => {
  const formattedPreferences = deleteCommonKeys(preferences);
  const values = Object.values(formattedPreferences);

  let count = 0;
  _.forEach(values, (value: any) => {
    if (value === true) {
      count++;
    } else if (typeof value === 'string' && value !== '') {
      count++;
    } else if (typeof value === 'object' && value?.length > 0) {
      count++;
    }
  });

  return count;
};

export const formatPreferenceData = (obj) => ({
  ...obj,
  virtualApptAvbl: obj?.virtualApptAvbl === 'y',
  onlineApptSchdlAvbl: obj?.onlineApptSchdlAvbl === 'y',
  evngApptAvbl: obj?.evngApptAvbl === 'y',
  wkndApptAvbl: obj?.wkndApptAvbl === 'y',
  providerBoardCertStatus: obj?.providerBoardCertStatus === 'y',
  providerGender:
    obj?.providerGender === '' ? [] : obj?.providerGender?.split(',') || [],
  languagesSpoken:
    obj?.languagesSpoken === '' ? [] : obj?.languagesSpoken?.split(',') || [],
});

export const formatPreferencePayload = (payload) => {
  const updatedPayload = JSON.parse(JSON.stringify(payload));

  delete updatedPayload.memberRandomizedKey;
  delete updatedPayload.createdDate;
  delete updatedPayload.lastUpdatedDate;
  delete updatedPayload.hasDefaultPreferences;

  return updatedPayload;
};

export const defaultToZero = (val: any): any =>
  val !== '' && val !== null ? val : '0';

export const removeLeadingZeroes = (str?: string): string =>
  str?.replace(/^0+/, '') ?? '';

export interface CustomPreferredPolicy {
  badgeLabel: string;
  description: string;
  icon: string;
  id: string;
  key: string;
  'lagoon.updateAt': string;
  'lagoon.updatedBy': string;
  policyId: string;
  popoverContent: string;
  productName: string;
  reciprocityId: string;
  byteCardLabel: string;
}

export const getIsCustomPreferredPolicy = (
  customPreferredPolicies: CustomPreferredPolicy[],
  policyId: string,
  reciprocityId: string
): boolean =>
  !!customPreferredPolicies?.find((cpp) => {
    if (cpp.productName === 'OBH CUSTOM NETWORK') {
      return cpp.reciprocityId === reciprocityId;
    }

    return (
      removeLeadingZeroes(cpp.policyId) === removeLeadingZeroes(policyId) &&
      cpp.reciprocityId === reciprocityId
    );
  });

export const getCustomPreferredPolicyData = (
  customPreferredPolicies: CustomPreferredPolicy[],
  policyId: string,
  reciprocityId: string
): CustomPreferredPolicy | undefined =>
  customPreferredPolicies?.find((cpp) => {
    if (cpp.productName === 'OBH CUSTOM NETWORK') {
      return cpp.reciprocityId === reciprocityId;
    }

    return (
      removeLeadingZeroes(cpp.policyId) === removeLeadingZeroes(policyId) &&
      cpp.reciprocityId === reciprocityId
    );
  });

export const useCustomPolicy = (
  selectedMember,
  tableFunction1
): {
  isCustomPreferredPolicy: boolean;
  customPreferredPolicyData: CustomPreferredPolicy | undefined;
} => {
  // const tableFunction1 = ;

  const data =
    tableFunction1 != null
      ? { ['custom-preferred-policies']: tableFunction1() }
      : null;
  const res = data != null ? data['custom-preferred-policies'] : [];

  const currentMember = selectedMember ?? {
    policyNumber: '',
    reciprocityId: '',
  };

  const isCustomPreferredPolicy: boolean = getIsCustomPreferredPolicy(
    res,
    currentMember?.policyNumber ?? '',
    currentMember?.reciprocityId ?? ''
  );

  const customPreferredPolicyData = getCustomPreferredPolicyData(
    res,
    currentMember?.policyNumber ?? '',
    currentMember?.reciprocityId ?? ''
  );

  return { isCustomPreferredPolicy, customPreferredPolicyData };
};

export const defaultStringToEmpty = (
  string: string | undefined | null
): string => string ?? '';

export const getAcoBenefitPlan = (
  cesProductCode?: string,
  memberMarketType?: string,
  membershipCategory?: string,
  population?: string,
  acoBenefitPlanCollection?: string
): boolean => {
  const isNotOxford = membershipCategory !== 'OXFORD';
  if (population === 'USP') {
    return isNotOxford;
  }
  const ACO_BENEFIT_COLLECTION =
    acoBenefitPlanCollection?.split(',')?.map((pair) => {
      const [marketType, productCode] = pair.split(':');
      return [marketType, productCode];
    }) ?? [];
  return ACO_BENEFIT_COLLECTION?.some(([marketType, productCode]) =>
    defaultStringToEmpty(cesProductCode).length > 0 &&
    defaultStringToEmpty(memberMarketType).length > 0
      ? productCode === cesProductCode && marketType === memberMarketType
      : false
  );
};

export const truncateString = (str: string = '', num: number): string => {
  if (num <= 0) {
    return '';
  }
  if (str.length > num) {
    return str.slice(0, num) + '...';
  } else {
    return str;
  }
};

export const distanceParser = (distance: string): string => {
  const number = parseFloat(distance);

  return isNaN(number)
    ? t('SMART_CHOICE.AUDIT_SCREEN.Number_of_miles', { number: '0' })
    : t('SMART_CHOICE.AUDIT_SCREEN.Number_of_miles', {
        number: number.toFixed(2).replace('.00', ''),
      });
};

export const getLanguageFormatted = (data: string) =>
  truncateString(
    data
      ?.split(',')
      ?.map((item) => enLangugages[item])
      ?.map((item) => removeSpecialCharactersUsingMatch(item ?? ''))
      ?.join(', ')
      ?.toString(),
    28
  );

export const formattedValue = (val: string | null | undefined): string =>
  val?.toLowerCase() === 'y'
    ? t('Yes')
    : val?.toLowerCase() === 'n'
    ? t('No')
    : t('N/A');

export const getLLMStatus = (llmStatus, t): String => {
  if (llmStatus?.toUpperCase() === 'NA') {
    return t('SMART_CHOICE.AUDIT_SCREEN.Not Applied');
  } else {
    return llmStatus === '1' ? t('Yes') : t('No');
  }
};

export const getFlagValue = (val: string | null | undefined) =>
  val === '' ? 'n' : val;

export const useSuppressPremiumSmartchoice = (state: string): string => {
  const suppressStates = useLagoon('config')()?.find(
    (row) => row.key === 'SMART_CHOICE_SUPRESS_PREMIUM'
  )?.value;

  return suppressStates
    ?.replace(/[\s,]+/g, ',')
    ?.split(',')
    ?.includes(state)
    ? 'Y'
    : 'N';
};

export const getSuppressCostEfficiencySmartChoice = (
  specialty: string
): string => {
  const suppressSpecialtiesLagoonData =
    useLagoon('suppress-smart-choice-ohpd-specialities')?.() ?? [];
  const suppressSpecialties = suppressSpecialtiesLagoonData?.flatMap((item) => [
    ...(item.medicalSpecialistsRollupCode
      ? item.medicalSpecialistsRollupCode.split(',').map((code) => code.trim())
      : []),
    ...(item.primaryCareRollupCode
      ? item.primaryCareRollupCode.split(',').map((code) => code.trim())
      : []),
  ]);
  const codes = Array.isArray(specialty)
    ? specialty
    : specialty.split(',').map((code) => code.trim());
  return suppressSpecialties.some((specialtyCode) =>
    codes.includes(specialtyCode)
  )
    ? 'N'
    : 'Y';
};
